import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Sparkles, Zap, Brain, Bot, Radar, Cpu } from "lucide-react";
import { useAuthContext } from "@/contexts/AuthContext";

const getAuthRedirectBase = () => {
  // ALWAYS route auth emails to production domain — never preview/lovable URLs
  return "https://app.roofgeniusai.com";
};

const Auth = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuthContext();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const authRedirectBase = getAuthRedirectBase();

  useEffect(() => {
    if (!authLoading && user) {
      navigate("/");
    }
  }, [authLoading, user, navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${authRedirectBase}/`,
        },
      });

      if (error) throw error;

      if (data.user) {
        toast.success("Account created successfully! Redirecting...");
        
        // Send welcome email
        setTimeout(() => {
          supabase.functions.invoke('send-welcome-email', {
            body: {
              email: data.user!.email,
              fullName: fullName || data.user!.email?.split('@')[0],
              orgName: 'RoofGenius',
            },
          }).catch(err => console.error('Failed to send welcome email:', err));
        }, 1000);
      }
    } catch (error: any) {
      console.error("Sign up error:", error);
      toast.error(error.message || "Failed to sign up");
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      if (data.user) {
        toast.success("Welcome back!");
      }
    } catch (error: any) {
      console.error("Sign in error:", error);
      toast.error(error.message || "Failed to sign in");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Please enter your email address first");
      return;
    }
    setLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${authRedirectBase}/reset-password`,
      });
      if (error) throw error;
      toast.success("Password reset email sent! Check your inbox.");
    } catch (error: any) {
      console.error("Password reset error:", error);
      toast.error(error.message || "Failed to send reset email");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#05060a] text-white">
      {/* Animated gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-[#22d3ee] opacity-20 blur-[120px] animate-pulse" />
        <div className="absolute top-1/3 -right-40 h-[600px] w-[600px] rounded-full bg-[#a855f7] opacity-25 blur-[140px] animate-pulse" style={{ animationDelay: "1s", animationDuration: "5s" }} />
        <div className="absolute -bottom-40 left-1/3 h-[500px] w-[500px] rounded-full bg-[#22c55e] opacity-20 blur-[130px] animate-pulse" style={{ animationDelay: "2s", animationDuration: "6s" }} />
      </div>

      {/* Grid overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.5) 1px,transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "radial-gradient(ellipse at center, black 40%, transparent 75%)",
        }}
      />

      {/* Scanline */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400 to-transparent opacity-60" />

      <div className="relative mx-auto grid min-h-screen w-full max-w-7xl items-center gap-12 px-6 py-10 lg:grid-cols-[1.1fr_460px] lg:px-12">
        {/* LEFT — Brand / hero */}
        <div className="hidden lg:block">
          <div className="mb-8 inline-flex items-center gap-3 rounded-full border border-white/10 bg-white/5 px-4 py-2 backdrop-blur-md">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
            </span>
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-white/70">AI Engine Online</span>
          </div>

          <h1 className="bg-gradient-to-br from-white via-white to-white/40 bg-clip-text text-7xl font-bold leading-[1.05] tracking-tight text-transparent">
            Roof<span className="bg-gradient-to-r from-cyan-300 via-fuchsia-400 to-emerald-300 bg-clip-text text-transparent">Genius</span>
            <span className="block text-4xl font-medium text-white/60">AI for the trades.</span>
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/60">
            Estimate, quote, and close faster than humanly possible. Bridgette runs your back office while you run the field.
          </p>

          <div className="mt-12 grid max-w-2xl grid-cols-2 gap-4">
            {[
              { icon: Brain, label: "Bridgette AI", desc: "Voice copilot for every job" },
              { icon: Zap, label: "Instant Quotes", desc: "Sub-minute proposals" },
              { icon: Radar, label: "Storm Watch", desc: "Real-time hail intel" },
              { icon: Cpu, label: "Auto Estimates", desc: "AI line items + margins" },
            ].map(({ icon: Icon, label, desc }) => (
              <div
                key={label}
                className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-md transition-all hover:border-cyan-400/40 hover:bg-white/[0.06]"
              >
                <div className="absolute -right-8 -top-8 h-24 w-24 rounded-full bg-gradient-to-br from-cyan-400/20 to-fuchsia-500/20 blur-2xl transition-opacity group-hover:opacity-100 opacity-0" />
                <div className="relative">
                  <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-400/20 to-fuchsia-500/20 ring-1 ring-white/10">
                    <Icon className="h-5 w-5 text-cyan-300" />
                  </div>
                  <div className="text-sm font-semibold text-white">{label}</div>
                  <div className="mt-0.5 text-xs text-white/50">{desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 flex items-center gap-6 text-xs text-white/40">
            <div className="flex items-center gap-2"><Bot className="h-4 w-4 text-emerald-400" /> AI powered</div>
            <div className="h-3 w-px bg-white/10" />
            <div>SOC2-ready</div>
            <div className="h-3 w-px bg-white/10" />
            <div>Used by 500+ contractors</div>
          </div>
        </div>

        {/* RIGHT — Auth card */}
        <div className="w-full max-w-md justify-self-center lg:justify-self-end">
          <div className="mb-6 lg:hidden">
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5 text-cyan-300" />
              <span className="text-xs font-medium uppercase tracking-widest text-white/70">RoofGenius AI</span>
            </div>
            <h1 className="bg-gradient-to-br from-white to-white/50 bg-clip-text text-4xl font-bold tracking-tight text-transparent">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-white/50">Your AI crew is waiting.</p>
          </div>

          {/* Glow wrapper */}
          <div className="relative">
            <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-cyan-400/40 via-fuchsia-500/30 to-emerald-400/40 opacity-70 blur-md" />
            <Card className="relative overflow-hidden border-white/10 bg-[#0b0d14]/90 text-white shadow-2xl backdrop-blur-xl">
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-300/60 to-transparent" />
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-2xl font-bold text-white">
                  <Sparkles className="h-5 w-5 text-cyan-300" />
                  Enter the workspace
                </CardTitle>
                <CardDescription className="text-white/50">
                  AI-powered roofing operations. No fluff.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="signin" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 h-12 sm:h-11 bg-white/5 border border-white/10">
                    <TabsTrigger
                      value="signin"
                      className="text-base sm:text-sm font-semibold text-white/60 data-[state=active]:!bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-fuchsia-500 data-[state=active]:!text-white data-[state=active]:shadow-[0_0_20px_-4px_rgba(34,211,238,0.6)]"
                    >
                      Sign In
                    </TabsTrigger>
                    <TabsTrigger
                      value="signup"
                      className="text-base sm:text-sm font-semibold text-white/60 data-[state=active]:!bg-gradient-to-r data-[state=active]:from-fuchsia-500 data-[state=active]:to-emerald-500 data-[state=active]:!text-white data-[state=active]:shadow-[0_0_20px_-4px_rgba(168,85,247,0.6)]"
                    >
                      Sign Up
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="signin">
                    <form onSubmit={handleSignIn} className="space-y-5 sm:space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label htmlFor="signin-email" className="text-sm text-white/70">Email</Label>
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          className="h-12 sm:h-11 text-base bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-cyan-400/50 focus-visible:border-cyan-400/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signin-password" className="text-sm text-white/70">Password</Label>
                        <div className="relative">
                          <Input
                            id="signin-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            className="h-12 sm:h-11 text-base pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-cyan-400/50 focus-visible:border-cyan-400/50"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-cyan-300 transition-colors"
                            tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="group relative w-full h-12 sm:h-11 text-base sm:text-sm font-semibold overflow-hidden bg-gradient-to-r from-cyan-500 via-fuchsia-500 to-emerald-500 text-white shadow-[0_0_30px_-6px_rgba(34,211,238,0.6)] hover:shadow-[0_0_40px_-4px_rgba(168,85,247,0.7)] transition-all border-0"
                      >
                        <span className="relative flex items-center justify-center gap-2">
                          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Zap className="h-4 w-4" />}
                          {loading ? "Authenticating…" : "Launch Workspace"}
                        </span>
                      </Button>
                      <button
                        type="button"
                        onClick={handleForgotPassword}
                        disabled={loading}
                        className="w-full text-sm text-white/40 hover:text-cyan-300 transition-colors"
                      >
                        Forgot your password?
                      </button>
                    </form>
                  </TabsContent>

                  <TabsContent value="signup">
                    <form onSubmit={handleSignUp} className="space-y-5 sm:space-y-4 mt-2">
                      <div className="space-y-2">
                        <Label htmlFor="signup-name" className="text-sm text-white/70">Full Name</Label>
                        <Input
                          id="signup-name"
                          type="text"
                          placeholder="John Doe"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          required
                          disabled={loading}
                          className="h-12 sm:h-11 text-base bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-400/50 focus-visible:border-fuchsia-400/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-email" className="text-sm text-white/70">Email</Label>
                        <Input
                          id="signup-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          required
                          disabled={loading}
                          className="h-12 sm:h-11 text-base bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-400/50 focus-visible:border-fuchsia-400/50"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-password" className="text-sm text-white/70">Password</Label>
                        <div className="relative">
                          <Input
                            id="signup-password"
                            type={showPassword ? "text" : "password"}
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            disabled={loading}
                            minLength={6}
                            className="h-12 sm:h-11 text-base pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-fuchsia-400/50 focus-visible:border-fuchsia-400/50"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((s) => !s)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-fuchsia-300 transition-colors"
                            tabIndex={-1}
                            aria-label={showPassword ? "Hide password" : "Show password"}
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        disabled={loading}
                        className="group relative w-full h-12 sm:h-11 text-base sm:text-sm font-semibold overflow-hidden bg-gradient-to-r from-fuchsia-500 via-cyan-500 to-emerald-500 text-white shadow-[0_0_30px_-6px_rgba(168,85,247,0.6)] hover:shadow-[0_0_40px_-4px_rgba(34,211,238,0.7)] transition-all border-0"
                      >
                        <span className="relative flex items-center justify-center gap-2">
                          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                          {loading ? "Creating account…" : "Activate AI Account"}
                        </span>
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <p className="mt-6 text-center text-xs text-white/30">
            © RoofGenius AI · Built for contractors who close.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Auth;